/**
 * Countdown Pricing - Single source of truth for all card pricing.
 * Calls the countdown-pricing edge function which queries eBay sold listings.
 *
 * Replaces JustTCG (raw) and Poketrace (graded).
 */

import { supabase } from './supabase';

/**
 * Fetch raw card pricing via Countdown API.
 * @param {Object} params
 * @param {string} params.name - Card name
 * @param {string} params.setName - Set name
 * @param {string} params.localId - Card number (e.g. "199/266")
 * @param {string} [params.variant] - e.g. "Holo", "Reverse Holo"
 * @returns {{ evidence, stats, searchQuery, totalListings } | null}
 */
export async function fetchPricing({ name, setName, localId, variant, specification }) {
  if (!name) return null;

  console.log(`[Pricing] Countdown raw: "${name}" set="${setName}" #${localId} variant="${variant || 'Normal'}"`);
  const start = performance.now();

  try {
    const { data, error } = await supabase.functions.invoke('countdown-pricing', {
      body: {
        cardName: name,
        setName: setName || '',
        setNumber: localId || '',
        variant: variant || '',
        specification: specification || '',
        isGraded: false,
      },
    });

    if (error) {
      console.error('[Pricing] Edge function error:', error);
      return null;
    }

    const elapsed = Math.round(performance.now() - start);
    console.log(`[Pricing] Countdown raw done: ${data?.totalListings || 0} listings, ${elapsed}ms`);

    return data || null;
  } catch (err) {
    console.error('[Pricing] Countdown raw error:', err.message);
    return null;
  }
}

/**
 * Fetch graded card pricing via Countdown API.
 * @param {Object} params
 * @param {string} params.name - Card name
 * @param {string} params.setName - Set name
 * @param {string} params.number - Card number
 * @param {string} params.condition - Grade string e.g. "PSA 10", "CGC 9.5"
 * @param {string} [params.variant] - Variant
 * @returns {{ evidence, stats, searchQuery, totalListings } | null}
 */
export async function fetchGradedPricing({ name, setName, number, condition, variant }) {
  if (!name || !condition) return { price: null, pricingData: null };

  // Parse company from condition string: "PSA 10" -> company="PSA"
  const parts = condition.trim().split(/\s+/);
  const company = parts[0] || 'PSA';

  console.log(`[Pricing] Countdown graded: "${name}" company=${company} condition="${condition}"`);
  const start = performance.now();

  try {
    const { data, error } = await supabase.functions.invoke('countdown-pricing', {
      body: {
        cardName: name,
        setName: setName || '',
        setNumber: number || '',
        variant: variant || '',
        isGraded: true,
        gradingCompany: company,
      },
    });

    if (error) {
      console.error('[Pricing] Edge function graded error:', error);
      return { price: null, pricingData: null };
    }

    const elapsed = Math.round(performance.now() - start);
    console.log(`[Pricing] Countdown graded done: ${data?.totalListings || 0} listings, ${elapsed}ms`);

    // Extract price for the specific grade
    const gradeKey = `${company.toUpperCase()} ${parts.slice(1).join(' ')}`;
    const gradeStats = data?.stats?.[gradeKey];
    const price = gradeStats?.med ?? gradeStats?.avg ?? null;

    return {
      price,
      pricingData: {
        isGraded: true,
        company: company.toUpperCase(),
        evidence: data?.evidence || [],
        stats: data?.stats || {},
        searchQuery: data?.searchQuery || '',
        totalListings: data?.totalListings || 0,
      },
    };
  } catch (err) {
    console.error('[Pricing] Countdown graded error:', err.message);
    return { price: null, pricingData: null };
  }
}
